import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { aboutInfo } from '@/mocks/videos';
import { siteConfig } from '@/mocks/site';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const orgSchema = {
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: aboutInfo.contact.phone,
      email: aboutInfo.contact.email,
      contactType: 'customer service',
    },
  };

  const faqSchema = {
    mainEntity: aboutInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const webPageSchema = {
    name: `关于我们 - ${siteConfig.name}`,
    description: `了解${siteConfig.name}的品牌故事、发展历程、核心价值观和联系方式。`,
    url: `${siteConfig.url}/about`,
  };

  return (
    <>
      <SchemaOrg type="Organization" data={orgSchema} />
      <SchemaOrg type="FAQPage" data={faqSchema} />
      <SchemaOrg type="WebPage" data={webPageSchema} />

      <Navbar />

      <main className="pt-16 min-h-screen bg-background-50">
        <section className="py-10 md:py-14 bg-background-900">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="mt-6 text-center max-w-2xl mx-auto">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">关于农民影视</h1>
              <p className="text-foreground-300 text-sm md:text-base leading-relaxed">
                {siteConfig.tagline}
              </p>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">品牌故事</h2>
                <p className="text-foreground-600 text-sm leading-relaxed mb-4">{aboutInfo.brandIntro}</p>
                <p className="text-foreground-600 text-sm leading-relaxed">{aboutInfo.mission}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-5 bg-primary-50 rounded-lg text-center">
                  <div className="text-3xl font-bold text-primary-600 mb-1">10万+</div>
                  <div className="text-xs text-foreground-500">影视资源</div>
                </div>
                <div className="p-5 bg-accent-50 rounded-lg text-center">
                  <div className="text-3xl font-bold text-accent-600 mb-1">500万+</div>
                  <div className="text-xs text-foreground-500">月活用户</div>
                </div>
                <div className="p-5 bg-secondary-100 rounded-lg text-center">
                  <div className="text-3xl font-bold text-secondary-600 mb-1">8+</div>
                  <div className="text-xs text-foreground-500">年行业经验</div>
                </div>
                <div className="p-5 bg-primary-50 rounded-lg text-center">
                  <div className="text-3xl font-bold text-primary-600 mb-1">4K</div>
                  <div className="text-xs text-foreground-500">超高清画质</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">核心价值观</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutInfo.values.map((v, i) => (
                <div key={i} className="p-6 bg-background-50 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className={`text-lg ${['ri-database-2-line', 'ri-4k-line', 'ri-speed-line', 'ri-gift-line'][i]}`}></i>
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">发展历程</h2>
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              {aboutInfo.milestones.map((m, i) => (
                <div key={i} className={`relative flex items-start gap-6 mb-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right md:pr-10' : 'md:text-left md:pl-10'}`}>
                    <div className="bg-background-100 rounded-lg p-4">
                      <span className="inline-block px-2.5 py-0.5 bg-primary-500 text-white text-xs font-bold rounded-md mb-2 whitespace-nowrap">{m.year}</span>
                      <p className="text-sm text-foreground-700 leading-relaxed">{m.event}</p>
                    </div>
                  </div>
                  <div className="hidden md:block flex-1"></div>
                  <div className="absolute left-0 md:left-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-white -translate-x-1/2 border-4 border-background-50 z-10">
                    <i className="ri-check-line text-xs"></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">用户口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {aboutInfo.testimonials.map((t, i) => (
                <div key={i} className="p-6 bg-background-50 rounded-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-bold text-sm">
                      {t.avatar}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <div className="flex items-center gap-0.5 mt-3">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-xs text-accent-500"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">常见问题</h2>
            <div className="max-w-2xl mx-auto flex flex-col gap-3">
              {aboutInfo.faqs.map((faq, i) => (
                <div key={i} className="bg-background-100 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                  >
                    <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.question}</span>
                    <div className={`w-6 h-6 flex items-center justify-center shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-foreground-500"></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">联系我们</h2>
            <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex items-start gap-4 p-5 bg-background-50 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                  <i className="ri-mail-line"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-400 mb-0.5">客服邮箱</div>
                  <div className="text-sm font-medium text-foreground-800">{aboutInfo.contact.email}</div>
                </div>
              </div>
              <div className="flex items-start gap-4 p-5 bg-background-50 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                  <i className="ri-phone-line"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-400 mb-0.5">客服热线</div>
                  <div className="text-sm font-medium text-foreground-800">{aboutInfo.contact.phone}</div>
                </div>
              </div>
              <div className="flex items-start gap-4 p-5 bg-background-50 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                  <i className="ri-map-pin-line"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-400 mb-0.5">公司地址</div>
                  <div className="text-sm font-medium text-foreground-800">{aboutInfo.contact.address}</div>
                </div>
              </div>
              <div className="flex items-start gap-4 p-5 bg-background-50 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                  <i className="ri-wechat-line"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-400 mb-0.5">微信公众号</div>
                  <div className="text-sm font-medium text-foreground-800">{aboutInfo.contact.wechat}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}